import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { HeatmapAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisDefinition';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ExploreSectionInterface } from 'apps/sigma2-ui/src/explore-section/components/ExploreSectionInterface';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { isEntitledToSummaryStats } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToTableView } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToGeolocation } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToHeatmap } from 'libs/end-to-end/src/group-entitlements';
import { useParams } from 'react-router';
import { TableViewFullComponent } from 'apps/sigma2-ui/src/explore-section/components/TableView';
import { SummaryStatsFullComponent } from 'apps/sigma2-ui/src/explore-section/components/SummaryStats';
import { GeolocationComponent } from 'apps/sigma2-ui/src/components/geolocation/GeolocationComponent';
import { isSummaryStatsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isTableViewEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isHeatmapEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isGeolocationEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

/**
 * Represents the available sections/tabs in the Explore page
 * @typedef {'heatmap'} ExplorePageSectionFilter
 */
type ExplorePageSectionFilter =
  | 'heatmap'
  | 'table-view'
  | 'summary-stats'
  | 'geolocation';

/**
 * Props for configuring tab display and behavior
 * @interface ExplorePageFilterValueProps
 * @property {ExplorePageSectionFilter} tabValue - The identifier for the tab
 * @property {string} displayedValue - The localized text to display for the tab
 * @property {string} [automationTag] - Optional tag used for automation/testing
 */
interface ExplorePageFilterValueProps {
  tabValue: ExplorePageSectionFilter;
  displayedValue: string;
  automationTag?: string;
}

/**
 * The Explore page component that displays heatmap analysis visualization
 * @component
 * @param {ExplorePageProps} props - Component props
 * @returns {JSX.Element} The rendered Explore page
 */
export const ExplorePage = () => {
  const language = useLanguage();
  const navigate = useNavigateSafe();
  const { userAuthenticatedApiCall } = useUser();
  const { myObservation, selectedOrganizationId } = useObservation();
  const { sectionName: sectionNameRaw } = useParams();

  useAuthenticatedPageRedirect();

  const heatmapEnabled = useMemo(() => {
    return (
      isHeatmapEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToHeatmap({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const summaryStatsEnabled = useMemo(() => {
    return (
      isSummaryStatsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToSummaryStats({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const tableViewEnabled = useMemo(() => {
    return (
      isTableViewEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToTableView({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const geolocationEnabled = useMemo(() => {
    return (
      isGeolocationEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToGeolocation({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const selectedTab: ExplorePageSectionFilter | null = useMemo(() => {
    const isValid = (name: ExplorePageSectionFilter) =>
      (name === 'heatmap' && heatmapEnabled) ||
      (name === 'table-view' && tableViewEnabled) ||
      (name === 'summary-stats' && summaryStatsEnabled) ||
      (name === 'geolocation' && geolocationEnabled);

    if (
      sectionNameRaw === 'heatmap' ||
      sectionNameRaw === 'table-view' ||
      sectionNameRaw === 'summary-stats' ||
      sectionNameRaw === 'geolocation'
    ) {
      return isValid(sectionNameRaw) ? sectionNameRaw : null;
    }

    if (heatmapEnabled) {
      return 'heatmap';
    }

    if (tableViewEnabled) {
      return 'table-view';
    }

    if (summaryStatsEnabled) {
      return 'summary-stats';
    }

    if (geolocationEnabled) {
      return 'geolocation';
    }

    return null;
  }, [
    sectionNameRaw,
    heatmapEnabled,
    tableViewEnabled,
    summaryStatsEnabled,
    geolocationEnabled,
  ]);

  const getExplorePageTabs = useMemo<ExplorePageFilterValueProps[]>(() => {
    return [
      ...(heatmapEnabled
        ? [
            {
              tabValue: 'heatmap' as const,
              displayedValue: language.heatmapPageTitle(),
              automationTag: 'heatmap',
            },
          ]
        : []),
      ...(tableViewEnabled
        ? [
            {
              tabValue: 'table-view' as const,
              displayedValue: language.tableViewPageTitle(),
              automationTag: 'table-view',
            },
          ]
        : []),
      ...(summaryStatsEnabled
        ? [
            {
              tabValue: 'summary-stats' as const,
              displayedValue: language.summaryStatsPageTitle(),
              automationTag: 'summary-stats',
            },
          ]
        : []),
      ...(geolocationEnabled
        ? [
            {
              tabValue: 'geolocation' as const,
              displayedValue: language.geolocationPageTitle(),
              automationTag: 'geolocation',
            },
          ]
        : []),
    ];
  }, [
    heatmapEnabled,
    language,
    tableViewEnabled,
    summaryStatsEnabled,
    geolocationEnabled,
  ]);

  return (
    <MainLayout showBackToTop>
      <Helmet>
        <title>{documentTitle(language.explorePageTitle())}</title>
      </Helmet>
      <PageSection>
        {selectedTab !== null && (
          <Spacing direction="vertical" size="md">
            <S2Typography
              variant="headline-3"
              translatedContent={language.explorePageTitle()}
            />

            <TabsRadioGroup
              currValue={selectedTab}
              values={getExplorePageTabs}
              onChange={(value: ExplorePageSectionFilter) => {
                if (value === 'heatmap') {
                  navigate('/explore/heatmap');
                } else if (value === 'table-view') {
                  navigate('/explore/table-view');
                } else if (value === 'summary-stats') {
                  navigate('/explore/summary-stats');
                } else if (value === 'geolocation') {
                  navigate('/explore/geolocation');
                } else {
                  throw guardExhaustive(value);
                }
              }}
              minWidth="auto"
              gap="md"
              underlineBgClass="bg-secondary"
              renderTab={(tab) => {
                const filter = tab.value as ExplorePageFilterValueProps;
                return (
                  <S2Typography
                    variant="headline-6-alt"
                    translatedContent={language.dangerousUserInput({
                      value: filter.displayedValue,
                    })}
                    dangerouslyOverrideClassNameFn={
                      filter.tabValue === selectedTab
                        ? replaceTextColor('text-secondary')
                        : undefined
                    }
                  />
                );
              }}
            />

            {myObservation &&
              selectedOrganizationId &&
              selectedTab === 'heatmap' && (
                <CustomViz
                  observation={myObservation.type}
                  organizationId={selectedOrganizationId}
                  customVizDefinition={HeatmapAnalysisCustomVizDefinition}
                  userAuthenticatedApiCall={userAuthenticatedApiCall}
                >
                  <ExploreSectionInterface
                    observation={myObservation}
                    organizationId={selectedOrganizationId}
                  />
                </CustomViz>
              )}

            {selectedTab === 'table-view' && <TableViewFullComponent />}

            {selectedTab === 'summary-stats' && <SummaryStatsFullComponent />}

            {selectedTab === 'geolocation' && <GeolocationComponent />}
          </Spacing>
        )}
      </PageSection>
    </MainLayout>
  );
};
